import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudRoundedIcon from "@mui/icons-material/CloudRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import PolicyRoundedIcon from "@mui/icons-material/PolicyRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { Box, Tooltip, Typography } from "@mui/material";
import React from "react";
import { STATUS_COLORS, UI } from "../tokens";

// Colors come from STATUS_COLORS (design tokens). Icons stay here since they are not
// tokens; a status icon is shown so meaning is never conveyed by color alone.
const STATUS_ICONS = {
  ready: CheckCircleRoundedIcon,
  processing: AutorenewRoundedIcon,
  noPolicies: PolicyRoundedIcon,
  alert: WarningRoundedIcon,
};

function TypeIcon({ root, isVertex, color }) {
  const Icon = root ? SmartToyRoundedIcon : isVertex ? HubRoundedIcon : CloudRoundedIcon;
  return <Icon sx={{ fontSize: 14, color, opacity: 0.6, flexShrink: 0 }} />;
}

// One field inside the hover tooltip: a small uppercase label over its value.
function TooltipField({ label, value, mono }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: UI.textInverse,
          opacity: 0.55,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: 1.4,
          color: UI.textInverse,
          opacity: 0.92,
          fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
          overflowWrap: "anywhere",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function NodeContent(props) {
  const {
    status = "processing",
    displayName,
    description,
    granularity,
    progress,
    maxValue,
    isVertex,
    id,
    serviceName,
    project,
    root,
  } = props;

  const { fill, ink } = STATUS_COLORS[status] || STATUS_COLORS.processing;
  const Icon = STATUS_ICONS[status] || STATUS_ICONS.processing;
  const spin = status === "processing";

  const hasMax = typeof maxValue === "number" && maxValue > 0;
  const pct = hasMax
    ? Math.min(100, Math.round(((progress || 0) / maxValue) * 100))
    : status === "ready"
      ? 100
      : 0;
  const progressLabel = hasMax ? `${progress ?? 0} / ${maxValue}` : `${progress ?? 0}`;

  return (
    <Tooltip
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: UI.textPrimary,
            borderRadius: 2,
            p: 1.25,
            maxWidth: 280,
            "& .MuiTooltip-arrow": { color: UI.textPrimary },
          },
        },
      }}
      title={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: UI.textInverse }}>
            {displayName}
          </Typography>
          <Box
            sx={{ height: "1px", backgroundColor: UI.textInverse, opacity: 0.14 }}
          />
          {!root ? (
            <>
              <TooltipField label="Service" value={serviceName} mono />
              <TooltipField label="Project" value={project} mono />
            </>
          ) : null}
          {description ? <TooltipField label="Description" value={description} /> : null}
        </Box>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.625,
          height: "100%",
          boxSizing: "border-box",
          px: 1.25,
          py: 1,
          borderRadius: UI.radiusCard,
          backgroundColor: fill,
          color: ink,
          border: `1px solid ${UI.border}`,
          boxShadow: "0 1px 2px rgba(16,24,40,0.10), 0 2px 6px rgba(16,24,40,0.06)",
          cursor: "pointer",
          transition: "box-shadow .15s ease, transform .15s ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 8px 18px rgba(16,24,40,0.16)",
          },
        }}
      >
        {/* title — full width so the complete process name is readable (wraps freely) */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.625 }}>
          <Icon
            sx={{
              fontSize: 17,
              color: ink,
              mt: "1px",
              flexShrink: 0,
              ...(spin ? { animation: "nodeSpin 1.4s linear infinite" } : {}),
            }}
          />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.2,
              color: ink,
              overflowWrap: "anywhere",
            }}
          >
            {displayName}
          </Typography>
        </Box>

        {/* meta: type + process id (left), granularity (right) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.75,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <TypeIcon root={root} isVertex={isVertex} color={ink} />
            {!root && id != null ? (
              <Typography
                sx={{ fontSize: 11, fontWeight: 600, color: ink, opacity: 0.72 }}
              >
                ID {id}
              </Typography>
            ) : null}
          </Box>
          {granularity ? (
            <Box
              sx={{
                flexShrink: 0,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                color: ink,
                backgroundColor: `${ink}1f`,
                borderRadius: "6px",
                px: 0.625,
                py: "1px",
                lineHeight: 1.5,
                whiteSpace: "nowrap",
              }}
            >
              {granularity}
            </Box>
          ) : null}
        </Box>

        {/* progress bar + count */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              flex: 1,
              height: 5,
              borderRadius: 3,
              backgroundColor: `${ink}22`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${pct}%`,
                backgroundColor: ink,
                opacity: 0.85,
                borderRadius: 3,
                transition: "width .3s ease",
              }}
            />
          </Box>
          <Typography
            sx={{ fontSize: 11, fontWeight: 700, color: ink, whiteSpace: "nowrap" }}
          >
            {progressLabel}
          </Typography>
        </Box>
        <style>{"@keyframes nodeSpin { to { transform: rotate(360deg); } }"}</style>
      </Box>
    </Tooltip>
  );
}
